import React from 'react';
import { 
  Box, 
  Text, 
  VStack, 
  HStack, 
  Image, 
  Button, 
  Icon, 
  SimpleGrid, 
  useColorModeValue,
  useColorMode
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { FaArrowRight, FaCalendarAlt, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import { GiCrossedSwords } from 'react-icons/gi';
import introVideo from '../media/introvideo1b&w.mov';
import person1 from "../media/person1.jpg";
import conferencePoster from "../media/unlockyourpurpose.JPG";
import freshfire from "../media/freshfire.jpeg";
import worship from "../media/worship.jpg";
import bibleStudy from "../media/bible-study.jpg";
import outreach from "../media/outreach.jpg";
import social from "../media/social.jpg";

const Home = () => {
  const location = useLocation();
  const controls = useAnimation();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  // ===== UPDATED COLOR SCHEME =====
  // For DARK MODE - Super bright & sharp
  const darkBlue = 'blue.400';  // Bright electric blue
  const darkRed = 'red.400';    // Vivid crimson red

  // For LIGHT MODE - Super dark & rich
  const lightBlue = 'blue.900'; // Deep navy blue
  const lightRed = 'red.800';   // Dark blood red

  // Apply them
  const secondaryColor = useColorModeValue(lightRed, darkRed);
  const bgColor = useColorModeValue('white', 'gray.800');
  const sectionBg = useColorModeValue('gray.50', 'gray.900');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const cardBg = useColorModeValue('white', 'gray.700');

  const mainGradient = useColorModeValue(
    `linear(to-r, ${lightBlue}, ${lightRed})`, // Dark blue to dark red (light)
    `linear(to-r, ${darkBlue}, ${darkRed})`    // Bright blue to bright red (dark)
  );

  const imageOverlay = useColorModeValue(
    'linear(to-b, rgba(30, 58, 138, 0.7), rgba(153, 27, 27, 0.7))', // Dark blue to dark red (light)
    'linear(to-b, rgba(96, 165, 250, 0.5), rgba(248, 113, 113, 0.5))' // Bright blue to bright red (dark)
  );
  // ===== END COLOR SCHEME =====

  useEffect(() => {
    if (location.state?.scrollToSection) {
      const section = document.getElementById(location.state.scrollToSection);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  useEffect(() => {
    controls.start("visible");
  }, [controls]);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const slideIn = (direction) => ({
    hidden: { opacity: 0, x: direction === 'left' ? -100 : 100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "backOut" } }
  });

  return (
    <VStack spacing={0} overflowX="hidden">
      {/* Hero Section with Video Background */}
      <Box position="relative" w="100%" h={{ base: "70vh", md: "90vh" }} overflow="hidden" >
        <Box
          as="video"
          src={introVideo}
          autoPlay
          loop
          muted
          playsInline
          objectFit="cover"
          w="100%"
          h="100%"
        />
        
        {/* Gradient Overlay - Updated to match new colors */}
        <Box
          position="absolute"
          top="0"
          left="0"
          w="full"
          h="full"
          bgGradient={imageOverlay}
          zIndex="1"
        />

        {/* Hero Content */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          textAlign="center"
          zIndex="2"
          px={5}
          w="full"
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <VStack spacing={4}>
              <motion.div variants={fadeIn}>
                <Text
                  fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
                  fontWeight="extrabold"
                  color="white"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                >
                  EMPOWERING GENERATIONS
                </Text>
              </motion.div>
              
              <motion.div variants={fadeIn}>
                <Text
                  fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                  fontWeight="bold"
                  color="white"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                >
                  REVEALING CHRIST
                </Text>
              </motion.div>
              
              <motion.div variants={fadeIn}>
                <Text
                  fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                  fontWeight="semibold"
                  color="white"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                >
                  INFLUENCING SOCIETY
                </Text>
              </motion.div>
              
              <motion.div variants={fadeIn}>
                <Button
                  as={RouterLink}
                  to="/about"
                  size="lg"
                  colorScheme="blue" // Changed to blue
                  rightIcon={<FaArrowRight />}
                  mt={8}
                  px={8}
                  py={6}
                  fontSize="xl"
                  borderRadius="full"
                  boxShadow="lg"
                  _hover={{ 
                    transform: "scale(1.05)",
                    bg: isDark ? "blue.300" : "blue.700" // Darker hover
                  }}
                  transition="all 0.2s"
                >
                  Discover More
                </Button>
              </motion.div>
            </VStack>
          </motion.div>
        </Box>
      </Box>

      {/* Photo Grid Section */}
      <Box w="full" py={16} bg={sectionBg}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={0}>
          {[worship, bibleStudy, outreach, social].map((img, index) => (
            <Box key={index} overflow="hidden" h="300px">
              <Image
                src={img}
                alt=""
                w="100%"
                h="100%"
                objectFit="cover"
                transition="all 0.4s"
                filter={isDark ? "brightness(0.9)" : "brightness(1)"}
                _hover={{ 
                  transform: "scale(1.05)",
                  filter: isDark ? "brightness(1.1)" : "brightness(1.1) saturate(1.1)"
                }}
              />
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Welcome Section */}
      <Box w="full" py={20} bg={bgColor} id="about">
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <HStack
              spacing={10}
              align="center"
              justify="space-between"
              flexDirection={{ base: "column-reverse", md: "row" }}
            >
              <motion.div variants={slideIn('left')}>
                <Box 
                  position="relative" 
                  borderRadius="2xl" 
                  overflow="hidden" 
                  boxShadow="xl"
                  transform="rotate(-1deg)"
                  _hover={{ transform: "rotate(0deg)" }}
                  transition="all 0.3s"
                >
                  <Image
                    src={person1}
                    alt="Alpha Youth Members"
                    height={{ base: "350px", md: "550px" }}
                    objectFit="cover"
                    filter={isDark ? "brightness(0.9)" : "brightness(1.05)"}
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    w="100%"
                    h="100%"
                    bgGradient={imageOverlay}
                  />
                </Box>
              </motion.div>

              <motion.div variants={slideIn('right')}>
                <VStack
                  align={{ base: "center", md: "start" }}
                  maxW={{ base: "100%", md: "50%" }}
                  spacing={8}
                >
                  <Text 
                    fontSize={{ base: "3xl", md: "5xl" }} 
                    fontWeight="extrabold" 
                    bgGradient={mainGradient}
                    bgClip="text"
                    color={textColor}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    WELCOME TO ALPHA YOUTH
                  </Text>
                  
                  <Text 
                    fontSize="xl" 
                    lineHeight="tall"
                    color={textColor}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Alpha Youth UK is a dynamic movement where young people encounter Christ, grow in faith, and change the world. We're not just another youth group - we're a family on mission to see this generation transformed by the power of the Gospel.
                  </Text>
                  
                  <HStack spacing={6} pt={4}>
                    <Button
                      as={RouterLink}
                      to="/about"
                      colorScheme="red" // Changed to red
                      rightIcon={<FaArrowRight />}
                      size="md"
                      px={8}
                      borderRadius="full"
                      boxShadow="md"
                      _hover={{ 
                        transform: "translateY(-3px)",
                        boxShadow: "lg",
                        bg: isDark ? "red.300" : "red.700" // Darker hover
                      }}
                    >
                      Our Story
                    </Button>
                    <Button
                      as={RouterLink}
                      to={{
                        pathname: "/about",
                        hash: "#core-values",
                        state: { scrollToSection: "core-values" }
                      }}
                      variant="outline"
                      colorScheme="blue" // Changed to blue
                      size="md"
                      px={8}
                      borderRadius="full"
                      rightIcon={<GiCrossedSwords />}
                      _hover={{ 
                        transform: "translateY(-3px)",
                        boxShadow: "md",
                        borderWidth: "2px"
                      }}
                    >
                      Our Beliefs
                    </Button>
                  </HStack>
                </VStack>
              </motion.div>
            </HStack>
          </motion.div>
        </Box>
      </Box>

      {/* Upcoming Event Section */}
      <Box w="full" py={20} bg={sectionBg} id="events">
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <VStack spacing={12} align="center">
              <motion.div variants={fadeIn}>
                <VStack spacing={3}>
                  <Text 
                    fontSize={{ base: "4xl", md: "5xl" }} 
                    fontWeight="black" 
                    textAlign="center"
                    bgGradient={mainGradient}
                    bgClip="text"
                    color={textColor}
                  >
                    UPCOMING EVENTS
                  </Text>
                </VStack>
              </motion.div>

              <HStack
                spacing={10}
                align="center"
                justify="space-between"
                flexDirection={{ base: "column", md: "row" }}
              >
                <motion.div variants={slideIn('left')}>
                  <Box 
                    position="relative"
                    borderRadius="2xl" 
                    overflow="hidden" 
                    boxShadow="2xl"
                    transform="rotate(2deg)"
                    _hover={{ transform: "rotate(0deg)" }}
                    transition="all 0.3s"
                  >
                    <Image
                      src={conferencePoster}
                      alt="Alpha Youth Conference 2025"
                      height={{ base: "350px", md: "550px" }}
                      objectFit="cover"
                      filter={isDark ? "brightness(0.9)" : "brightness(1)"}
                    />
                  </Box>
                </motion.div>

                <motion.div variants={slideIn('right')}>
                  <VStack
                    align={{ base: "center", md: "start" }}
                    maxW={{ base: "100%", md: "50%" }}
                    spacing={8}
                  >
                    <Text 
                      fontSize={{ base: "3xl", md: "4xl" }} 
                      fontWeight="extrabold" 
                      color={textColor}
                      textAlign={{ base: "center", md: "left" }}
                    >
                      Alpha Youth Conference 2025
                    </Text>
                    
                    <Text 
                      fontSize="xl" 
                      lineHeight="tall"
                      color={textColor}
                      textAlign={{ base: "center", md: "left" }}
                    >
                      Our annual conference is the highlight of the year! Expect powerful worship, life-changing messages, and an encounter with God that will set your faith on fire.
                    </Text>
                    
                    <VStack 
                      align="start" 
                      spacing={4} 
                      w="full" 
                      bg={cardBg} 
                      p={6} 
                      borderRadius="xl" 
                      boxShadow="md"
                    >
                      <HStack>
                        <Icon as={FaCalendarAlt} color={secondaryColor} boxSize={6} />
                        <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                          Saturday, 15th November 2025
                        </Text>
                      </HStack>
                      <HStack>
                        <Icon as={FaClock} color={secondaryColor} boxSize={6} />
                        <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                          4:00 PM - 9:00 PM
                        </Text>
                      </HStack>
                      <HStack align="start">
                        <Icon as={FaMapMarkerAlt} color={secondaryColor} boxSize={6} mt={1} />
                        <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                          WESTFIELD STRATFORD, VUE CINEMA, LONDON, E20 1EJ LEVEL 2 - FOOD COURT
                        </Text>
                      </HStack>
                    </VStack>
                  </VStack>
                </motion.div>
              </HStack>

              <HStack
                spacing={10}
                align="center"
                justify="space-between"
                flexDirection={{ base: "column", md: "row" }}
              >
                <motion.div variants={slideIn('left')}>
                  <Box 
                    position="relative"
                    borderRadius="2xl" 
                    overflow="hidden" 
                    boxShadow="2xl"
                    transform="rotate(2deg)"
                    _hover={{ transform: "rotate(0deg)" }}
                    transition="all 0.3s"
                  >
                    <Image
                      src={freshfire}
                      alt="Alpha Youth Conference 2025"
                      height={{ base: "350px", md: "550px" }}
                      objectFit="cover"
                      filter={isDark ? "brightness(0.9)" : "brightness(1)"}
                    />
                  </Box>
                </motion.div>

                <motion.div variants={slideIn('right')}>
                  <VStack
                    align={{ base: "center", md: "start" }}
                    maxW={{ base: "100%", md: "50%" }}
                    spacing={8}
                  >
                    <Text 
                      fontSize={{ base: "3xl", md: "4xl" }} 
                      fontWeight="extrabold" 
                      color={textColor}
                      textAlign={{ base: "center", md: "left" }}
                    >
                      All Night: Fresh Fire
                    </Text>
                    
                    <Text 
                      fontSize="xl" 
                      lineHeight="tall"
                      color={textColor}
                      textAlign={{ base: "center", md: "left" }}
                    >
                      This is a youth prayer night designed to ignite a fresh appetite for prayer among young people. 
                      The theme “Fresh Fire” reflects our desire to stir passion, spiritual hunger, and boldness through prayer.
                    </Text>
                    
                    <VStack 
                      align="start" 
                      spacing={4} 
                      w="full" 
                      bg={cardBg} 
                      p={6} 
                      borderRadius="xl" 
                      boxShadow="md"
                    >
                      <HStack>
                        <Icon as={FaCalendarAlt} color={secondaryColor} boxSize={6} />
                        <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                          Friday, 19th Sepetember 2025
                        </Text>
                      </HStack>
                      <HStack>
                        <Icon as={FaClock} color={secondaryColor} boxSize={6} />
                        <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                          11:30 PM - 3:00 AM
                        </Text>
                      </HStack>
                      <HStack align="start">
                        <Icon as={FaMapMarkerAlt} color={secondaryColor} boxSize={6} mt={1} />
                        <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                          248 London Rd, Romford RM7 9EL
                        </Text>
                      </HStack>
                    </VStack>
                    
                  </VStack>
                </motion.div>
              </HStack>

              
            </VStack>
          </motion.div>
        </Box>
      </Box>

      {/* Call to Action Section */}
      <Box w="full" py={20} bgGradient={mainGradient}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <VStack spacing={8} maxW="800px" mx="auto" px={4} textAlign="center">
            <Text 
              fontSize={{ base: "4xl", md: "5xl" }} 
              fontWeight="black" 
              color="white"
              textShadow="0 2px 8px rgba(0,0,0,0.3)"
            >
              READY TO BE PART OF SOMETHING BIGGER?
            </Text>
            
            <Text 
              fontSize="2xl" 
              color="whiteAlpha.900"
              fontWeight="medium"
              textShadow="0 1px 4px rgba(0,0,0,0.2)"
            >
              God is moving in this generation, and we'd love for you to join us on this adventure of faith!
            </Text>
            
            <HStack spacing={6} pt={8} wrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/contact"
                size="lg"
                colorScheme="whiteAlpha"
                rightIcon={<FaArrowRight />}
                px={10}
                py={6}
                borderRadius="full"
                _hover={{ 
                  transform: "scale(1.05)",
                  bg: isDark ? "whiteAlpha.800" : "whiteAlpha.600",
                  color: isDark ? "gray.800" : "white"
                }}
              >
                GET CONNECTED
              </Button>
            </HStack>
          </VStack>
        </motion.div>
      </Box>
    </VStack>
  );
};

export default Home;